// react core
import { useContext } from "react";

// material design
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// entzy resources
import configEntzy from "components/config/ConfigEntzy";
import InputForm from "components/input/InputForm";

// entzy context and services
import { MainContext } from "components/main/MainContext";
import { serviceObjectImageState } from "services/storage/object";
import { scrollSnapTop, timeout } from "models/Tools";

// fonts and icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage as iconImageFallback } from "@fortawesome/pro-thin-svg-icons";
import { faPaperPlane as iconPost } from "@fortawesome/pro-duotone-svg-icons";

function MemberPostMessage(props) {
  const mainContext = useContext(MainContext);
  const user = props.user;
  const roomArea = props.roomArea;
  const roomName = props.roomName;
  const roomId = props.roomId;
  const memberName = props.memberName ? props.memberName.toLowerCase() : "";

  const handlePostActionCallback = async (data) => {
    const messageText = data.filter((obj) => obj.id === "message-text")[0]
      .value;
    const messageImage = data.filter((obj) => obj.id === "message-image")[0]
      .value;
    // validate message content
    const hasText = messageText && messageText.length;
    const hasImage =
      messageImage && messageImage.key && messageImage.key.length;
    if (!hasText) {
      return {
        alert: true,
        message: "Add some message text to post",
      };
    }

    // validate image if available
    if (hasImage) {
      let count = 0,
        ready = false;
      if (messageImage.key === configEntzy.EMPTY_STRING_SET) {
        // this is a remove image request
        messageImage.preload = null;
        ready = true;
      } else {
        // for new image first check all resized images are available
        do {
          await timeout(count * 1000);
          ready = await serviceObjectImageState(messageImage.key);
          count++;
        } while (count < 10 && !ready);
      }
      // proceed when ready
      if (!ready) {
        return {
          alert: true,
          message:
            "Image could not be processed. Give it another go or if this problem persists try another image size or format.",
        };
      }
    }

    // preload message for instant UX
    const preloadMemberMessage = {
      preload: true,
      RoomId: roomId,
      RoomArea: roomArea,
      RoomName: roomName,
      Category: "runner",
      ContentType: "message",
      ContentData: messageText,
      PostId: "preload-" + new Date().getTime(),
      UserId: user.identity,
      FirstCreated: new Date().toISOString(),
      LastUpdated: new Date().toISOString(),
    };
    mainContext.postMemberMessage(preloadMemberMessage);
    props.handleCloseAction();
    scrollSnapTop();

    // prepare and post message
    const messagePayload = await mainContext.preparePostMemberMessage({
      RoomArea: roomArea,
      RoomName: roomName,
      ContentType: "message",
      ContentData: messageText,
      ContentImage: messageImage.key,
      ImagePreload: messageImage.preload,
    });

    // remove preload message regardless of post success
    if (messagePayload.alert) {
      preloadMemberMessage.alert = messagePayload.alert;
      preloadMemberMessage.message = messagePayload.message;
      preloadMemberMessage.message += " (not posted, try again)";
      mainContext.updateMemberMessage({
        roomId: roomId,
        data: preloadMemberMessage,
      });
      mainContext.updateAlert(messagePayload);
      // props.handleOpenAction();
    } else {
      mainContext.postMemberMessage(messagePayload.data);
      mainContext.removeMemberMessage({
        roomId: roomId,
        data: preloadMemberMessage,
      });
      // props.handleCloseAction();
    }
    return {
      alert: messagePayload.alert,
      message: messagePayload.message,
    };
  };

  return (
    <Box className="box-default">
      <Box
        className="box-default"
        sx={{
          pt: configEntzy.APP_SPACING_MD,
        }}
      >
        <Typography variant="subtitle2" color="primary">
          DM to @{memberName}
        </Typography>
      </Box>
      <Box
        className="box-default"
        sx={{
          pt: configEntzy.APP_SPACING_MD,
          pl: configEntzy.APP_SPACING_MD,
          pr: configEntzy.APP_SPACING_MD,
        }}
      >
        {configEntzy.RESERVED_NAMES.includes(memberName) ? (
          <Box
            className="box-default"
            sx={{
              pt: configEntzy.APP_SPACING_MD,
              pb: configEntzy.APP_SPACING_LG,
            }}
          >
            <Typography variant="body2" color="textSecondary">
              @{memberName} is not yet available for DMs
            </Typography>
          </Box>
        ) : (
          <InputForm
            user={user}
            navigate={props.navigate}
            fields={[
              {
                id: "message-text",
                type: "text-multiline",
                // label: "Enter message",
                placeholder: "Enter message...",
                value: "",
                width: "80%",
                required: true,
                maxLength: 1000,
                disabled: !user.connected,
                disableMargin: true,
              },
              {
                id: "message-image",
                type: "image-picker",
                // label: "Post with image",
                value: "",
                width: "20%",
                image: null,
                disabled: !user.connected,
                disablePadding: true,
                disableMargin: true,
              },
            ]}
            submitText={
              <span>
                <span>&nbsp;&nbsp;&nbsp;</span>
                <span>Post</span>
                <span>&nbsp;&nbsp;</span>
                <span>
                  <FontAwesomeIcon icon={iconPost} />
                </span>
              </span>
            }
            submitBoxSx={{
              pt: configEntzy.APP_SPACING_MD,
              pb: configEntzy.APP_SPACING_MD,
            }}
            formBgColor="white"
            fallbackImage={iconImageFallback}
            fallbackImageHidden={true}
            disabledSubmit={!user.connected}
            disabledTitle="Connect to continue"
            disabledText="Tap here to activate messaging"
            disabledClick={props.drawerConnectToggle}
            callback={handlePostActionCallback}
          />
        )}
      </Box>
    </Box>
  );
}

export default MemberPostMessage;
